/**
 * English site copy. zh-TW.ts, ja.ts, ko.ts and th.ts must match this shape (see Copy).
 * Multi-line headings use '\n' and render with white-space: pre-line.
 */

import { LOCALE_INFO } from './locale';

export const en = {
  htmlLang: LOCALE_INFO.en.htmlLang,
  /** Space between two phrases set on separate lines (e.g. hero heading): a space in English. */
  phraseGap: ' ',
  ogLocale: 'en_US',
  languageName: 'English',

  nav: {
    home: 'Travo home',
    primary: 'Primary',
    features: 'Features',
    plan: 'Plan a trip',
    about: 'About us',
    download: 'Download app',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    language: 'Language',
    byLabs: 'by Travo Labs'
  },

  download: {
    appStore: 'Download on the App Store',
    playStore: 'Get it on Google Play',
    ios: 'iOS (App Store)',
    android: 'Android (Google Play)',
    free: 'FREE · iOS & ANDROID'
  },

  seo: {
    /** 1200×630 share card in public/. */
    ogImage: '/og-image.png',
    ogImageAlt: 'Travo — Life moves fast. Travo remembers. A travel tracker, passport and trip planner for iOS and Android.',
    home: {
      title: 'Travo: Travel Tracker, Passport & Trip Planner App',
      description:
        'Travo starts your trips on its own, logs every city and country, stamps a passport for each new country, and helps you plan the next trip day by day. Free on iOS and Android.',
      keywords:
        'automatic travel tracker, travel passport app, countries visited, trip planner, itinerary app, travel diary, check-ins, share travel plans, travel map, Travo'
    },
    about: {
      title: 'About Travo: Preserving Your Story, Every Day',
      description:
        "We built Travo because life moves fast and your best moments shouldn't be forgotten. Travo tracks your trips hands-free, keeps the moments you choose, and helps you plan the next one.",
      keywords:
        'about Travo, Travo Labs mission, automatic travel tracking, travel passport, trip planning, travel diary, privacy-first travel app'
    }
  },

  hero: {
    eyebrow: 'Trips find you',
    titleLead: 'Life moves fast.',
    titleAccent: 'Travo remembers.',
    body: 'Leave home and Travo starts the trip for you — logging every city and country, stamping your passport, and keeping the plans for your next one. You just go.',
    photoAlt: 'Hot air balloons over a valley at sunrise',
    phoneAlt: 'Travo home map of Taipei with photo check-in pins and the Travel and F1 map modes',
    stampLabel: 'PAR AVION'
  },

  band: ['Map', 'Trips', 'Check-ins', 'Places', 'Friends', 'Passport', 'Itinerary', 'Discover'],

  features: {
    title: 'Eight stops.\nOne travel companion.',
    body: 'Remember where you’ve been, keep the moments that matter, and plan what’s next. Your journey with Travo starts with these eight stops.',
    items: [
      {
        tag: '01 · MAP',
        title: 'Your travels, together',
        body: 'Trips, saved places, check-ins, and friends — all on one map. Tap to switch layers and watch your world fill in as you explore.'
      },
      {
        tag: '02 · TRIPS',
        title: 'Just go. Travo remembers.',
        body: 'Leave home, and your trip starts automatically. Travo records the neighborhoods, cities, and countries you visit. Nothing to type.'
      },
      {
        tag: '03 · CHECK-INS',
        title: 'Keep the moment',
        body: 'Check in up to three times a day. Add a photo to remember the moment and the place.'
      },
      {
        tag: '04 · PLACES',
        title: 'Save it for later',
        body: 'Collect places in lists for your next trip, or bring a saved Google Maps list into Travo with a single share.'
      },
      {
        tag: '05 · FRIENDS',
        title: 'Share the journey',
        body: 'See your friends’ latest check-ins and exchange stamp cards with a quick QR scan.'
      },
      {
        tag: '06 · PASSPORT',
        title: 'A passport of memories',
        body: 'Collect a stamp for every country you visit. Log your flights and see how far you’ve flown.'
      },
      {
        tag: '07 · ITINERARY',
        title: 'Make a plan together',
        body: 'Build a day-by-day itinerary with saved places, stays, and flights. Invite friends to plan with you.'
      },
      {
        tag: '08 · DISCOVER',
        title: 'Find your next adventure',
        body: 'Explore itineraries shared by other travelers. Add one to your trip and make it your own.'
      }
    ],
    extrasLabel: 'ALSO IN THE BOX',
    extras: [
      'Home-screen widgets: Passport, Upcoming trip, Itinerary',
      'Monthly recap to share',
      'F1 plugin: races, results & standings on your map'
    ]
  },

  checkins: {
    eyebrow: '03 · CHECK-INS',
    title: 'Stamp your day.\nUp to three of them.',
    body: 'Travo logs the everyday on its own. Check-ins are the part you choose — add a photo and the map remembers exactly where you stood. A few a day, and it becomes your travel diary.',
    stamps: ['A great meal', 'A scenic spot', 'A fun hangout'],
    screenshotAlt: 'Daily Highlights calendar for June 2026 with photo check-ins and an Iceland trip'
  },

  passport: {
    eyebrow: '06 · PASSPORT',
    title: 'Every country gets a stamp.',
    body: 'Your passport fills itself in as you travel — a page of stamps for every year, with the countries and days away counted for you.',
    points: [
      'A stamp page for each year, with countries and days away counted',
      'Flights logged to your passport, with kilometres flown',
      'A monthly recap of your photos and places, ready to share',
      'A Passport widget for your home screen'
    ],
    sample: 'SAMPLE',
    thailand: 'Thailand',
    indonesia: 'Indonesia',
    japanAlt: 'Sample stamp: Japan, Kyoto',
    taiwanAlt: 'Sample stamp: Taiwan, Taipei'
  },

  plan: {
    eyebrow: '07 & 08 · ITINERARY + DISCOVER',
    title: "Plan the days. Or start from someone's plan.",
    body: 'Turn saved places into a day-by-day itinerary — plans, stays and flights in one place. Share it and friends can edit too. Or browse Discover, add a trip someone already planned, and make it yours.',
    points: [
      'Plans, stays and flights on one day-by-day timeline',
      'A reservation checklist for everything still to book',
      'A home-screen widget that shows what is on now and next',
      'Discover: search plans by country and length, then add one to your trip'
    ],
    widget: {
      day: 'DAY 2/10 · GOLDEN CIRCLE',
      label: 'WIDGET',
      nowLabel: 'NOW · 09:30',
      now: 'Þingvellir National Park',
      nextLabel: 'NEXT · 12:30',
      next: 'Geysir & Strokkur',
      later: 'Later today: Gullfoss',
      more: '+1 more'
    },
    discover: {
      kicker: 'DISCOVER · ICELAND',
      length: '10 DAYS',
      title: 'Iceland 10 Days Road Trip',
      byline: 'by Travo (@travo) · 999 added',
      cta: 'Add to my trip'
    }
  },

  how: {
    eyebrow: 'THE ROUTE',
    title: 'Set it up once. Then just go.',
    steps: [
      {
        tag: 'HOME BASE',
        title: 'Tell Travo where home is',
        body: 'So it knows when you are traveling and when you are back.'
      },
      {
        tag: 'DEPART',
        title: 'Grant location once',
        body: 'Battery-friendly background tracking. You can turn it off any time in Settings.'
      },
      {
        tag: 'IN TRANSIT',
        title: 'Leave home',
        body: 'A trip starts on its own, and every city and country is logged as you go.'
      },
      {
        tag: 'DELIVERED',
        title: 'Stamp, plan, share',
        body: 'Check in your best moments, fill your passport, and plan the next trip with friends.'
      }
    ],
    note: 'Already traveled? Travo can detect past trips from your photo library. It reads only where each photo was taken — your photos are never uploaded or stored.'
  },

  friends: {
    eyebrow: '05 · FRIENDS',
    title: 'Travel together, even apart.',
    body: 'Add friends and see where they checked in last — right on your map. Swap stamp cards with a QR code, or send your profile link. Scan to follow the route.',
    to: "TO: everyone you've traveled with",
    from: 'FROM: app.travolabs.com/@travo',
    cta: 'Watch demo video',
    caption: "GREETINGS FROM EVERYWHERE YOU'VE BEEN",
    mapAlt: 'World map with a passport, camera and travel keepsakes'
  },

  cta: {
    eyebrow: 'PAR AVION · FIRST CLASS',
    title: 'Your passport is waiting.',
    body: "Download Travo free. Your daily routines, weekend escapes and the trip you're planning next — all in one place."
  },

  footer: {
    tagline: 'Remember every journey. Plan the next one.',
    product: 'PRODUCT',
    community: 'COMMUNITY',
    legal: 'LEGAL',
    privacy: 'Privacy policy',
    terms: 'Terms of use',
    rights: 'TRAVO LABS. ALL RIGHTS RESERVED.'
  },

  about: {
    eyebrow: 'About Travo Labs',
    titleLead: 'Life moves fast.',
    titleAccent: 'Travo remembers.',
    intro: [
      'We built Travo to help you hold on to the places and moments that make life yours. From familiar streets to faraway cities, it quietly records your journey — so you can stay present as it unfolds.',
      'Trips start automatically. Each country you visit adds a stamp to your travel passport. The places you save become the beginnings of your next adventure.',
      'Open Travo to revisit where you’ve been, catch up on your friends’ travels, and plan where you’ll go next.'
    ],
    photoAlt: 'A city skyline at night',
    caption: 'GREETINGS FROM THE ROAD',
    captionTag: 'NO. 00 · ABOUT',
    stampLabel: 'PAR AVION',
    missionEyebrow: 'OUR MISSION',
    missionLead: "Make preserving your life's journey ",
    missionAccent: 'effortless',
    missionTail: ' and meaningful.',
    salutation: 'Dear traveler,',
    letter: [
      "We believe the best travel app is the one you forget you're using. Travo notices when you leave home and starts the trip for you, logging every neighborhood, city and country — from your own street to the other side of the world.",
      "The rest is yours to choose: a few check-ins for the moments that mattered, a passport that fills with stamps, a plan you build with friends or borrow from another traveler. You focus on the trip; Travo keeps the record. Years from now, you'll have every adventure, big and small, exactly where it happened."
    ],
    signature: '— Travo',
    whyTitle: 'Why choose Travo?',
    whyBody: 'Four promises, stamped and sealed.',
    reasons: [
      {
        title: 'Trips find you',
        body: 'Leave your home base and a trip starts on its own. Every city and country is logged, and your passport collects the stamps.'
      },
      {
        title: 'Moments you choose',
        body: 'Up to three check-ins a day, with photos pinned where you stood, become a travel diary worth rereading.'
      },
      {
        title: 'Plan it together',
        body: 'Save places, build a day-by-day itinerary friends can edit, or start from a plan another traveler published.'
      },
      {
        title: 'Private by design',
        body: 'Tracking is yours to turn off any time. Past-trip detection reads only where photos were taken — they never leave your phone.'
      }
    ],
    ctaEyebrow: 'YOUR NEXT POSTMARK',
    ctaTitle: "Live it. We'll write it down.",
    ctaBody: 'Travo is free on iOS and Android. Grant permission once and your map starts filling itself in.',
    breadcrumbAbout: 'About Us'
  },

  schema: {
    appDescription:
      'Hands-free travel tracker, passport and trip planner. Trips start automatically when you leave your home base, every city and country is logged, and each new country adds a passport stamp. Check in up to three moments a day, plan day-by-day itineraries with friends, or start from a published plan on Discover.',
    featureList: [
      'Automatic trips that start when you leave your home base',
      'Check-ins — up to three photo moments a day',
      'Travel passport with a stamp for every country and flights flown',
      'Day-by-day itineraries with stays, flights and shared editing',
      'Discover published travel plans and add them to your trip',
      'Saved place lists with Google Maps import',
      'Friends map and QR stamp cards',
      'Past-trip detection from photo locations',
      'Home-screen widgets and monthly recaps'
    ],
    videoName: 'See Travo in Action — Automatic Travel Tracker Demo',
    videoDescription:
      'Watch how Travo automatically tracks your daily routes and grand adventures, and builds an interactive map of everywhere you have been.'
  },

  notFound: {
    title: 'Page not found | Travo',
    eyebrow: 'RETURN TO SENDER',
    heading: "This address doesn't exist.",
    body: "The page you're looking for may have moved. Head back home and pick up the route from there.",
    cta: 'Back to home'
  },

  faqSection: {
    eyebrow: 'QUESTIONS',
    title: 'Questions, answered.',
    body: 'The short version of what Travo does, what it costs and who sees your travels.'
  },

  faq: [
    {
      question: 'What is Travo?',
      answer:
        'Travo is a free travel tracker, passport and trip planner app for iOS and Android, made by Travo Labs. It starts a trip on its own when you leave home, logs every city and country you visit, stamps a passport for each new country, and helps you plan the next trip day by day with friends.'
    },
    {
      question: 'What languages is Travo available in?',
      answer:
        'The Travo app and this website are available in English, Traditional Chinese, Japanese, Korean and Thai.'
    },
    {
      question: 'How does Travo track my travels?',
      answer:
        'Travo uses battery-friendly background location. Once you set your home base, leaving it starts a trip automatically, and every neighborhood, city and country you pass through is logged — nothing to type.'
    },
    {
      question: 'Is Travo free?',
      answer: 'Yes. Travo is free to download and use on iOS and Android.'
    },
    {
      question: 'Does Travo drain my battery?',
      answer:
        'No. Background tracking is battery-optimized, and you can turn it off any time in Settings.'
    },
    {
      question: 'What are check-ins?',
      answer:
        'You get up to three check-ins a day for the moments you want to keep. Add a photo and the map remembers where you stood; together they become your travel diary.'
    },
    {
      question: 'What is the Travo passport?',
      answer:
        'Every country you visit adds a stamp to your passport. Each year gets its own page with countries and days away counted, and flights you log add up to kilometres flown.'
    },
    {
      question: 'Can I plan trips in Travo?',
      answer:
        'Yes. Turn saved places into a day-by-day itinerary with stays and flights, share it so friends can edit, or add a published plan from Discover to your own trip.'
    },
    {
      question: 'Can Travo add trips I took before installing it?',
      answer:
        'Yes. Past-trip detection reads where your photos were taken to suggest earlier trips. Your photos are never uploaded or stored.'
    },
    {
      question: 'Who can see my travels?',
      answer:
        'You decide. Your location data is private, you choose which friends can follow you, and tracking can be turned off any time in Settings.'
    }
  ]
};

export type Copy = typeof en;
